import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { IMobileFlow, useOfferMode } from "@nju/data-access-portal-offers";
import {
  IFSecureOrchestrator,
  IFtthOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { portalRoutes } from "@nju/routes";
import { GeneralErrorNotification, Panel, Separator, Text } from "@nju/ui";
import { Buttons } from "./Buttons";
import { getStatusData } from "./getStatusData";
import { orderCTABannerMachine } from "./orderCTABanner.machine";

export interface ICTABannerProps {
  orchestrator:
    | IFtthOrchestrator
    | IMultiOrderOrchestrator
    | IFSecureOrchestrator;
  flow?: IMobileFlow | undefined;
}

export function OrderCTABanner({ orchestrator, flow }: ICTABannerProps) {
  const { reload, push } = useRouter();
  const mode = useOfferMode();

  const [current, send] = useMachine(orderCTABannerMachine, {
    context: {
      orchestrator,
      mode,
      flow,
    },
    actions: {
      redirectToSelectNewOrExistingNumberPage: () => {
        if (orchestrator.offerId) {
          push(
            portalRoutes.mojeKonto.anulujZamowienie.get({
              mode: "mobile",
              flow: "multiOrder",
            }),
          );
        }
      },
    },
  });

  const handleConfirmMigration = React.useCallback(() => {
    send({ type: "MIGRATION_CONFIRM" });
  }, [send]);

  const handleCancelOrder = React.useCallback(() => {
    send({ type: "CANCEL_ORDER" });
  }, [send]);

  const panelProps = React.useMemo(() => {
    return getStatusData({
      orchestrator,
    });
  }, [orchestrator]);

  const refresh = React.useCallback(() => {
    reload();
  }, [reload]);

  if (!panelProps) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  if (current.matches("error")) {
    return <GeneralErrorNotification callback={refresh} />;
  }

  const { text, title, iconName } = panelProps;
  const isConfirmMigrationLoading = current.matches("migrate");
  const isCancelFailedMigrationLoading = current.matches("cancelOrder");

  return (
    <Panel iconName={iconName} testId="ftth-order-status-banner" title={title}>
      <Separator size="xs" tone="invisible" />
      <Text
        className="whitespace-pre-line"
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "l",
        }}
      >
        {text}
      </Text>
      <Separator size="xs" tone="invisible" />
      <Buttons
        flow={flow}
        handleCancelFailedMigration={handleCancelOrder}
        handleConfirmMigration={handleConfirmMigration}
        isCancelFailedMigrationLoading={isCancelFailedMigrationLoading}
        isConfirmMigrationLoading={isConfirmMigrationLoading}
        orchestrator={orchestrator}
        refreshCallback={refresh}
      />
    </Panel>
  );
}
